/**
 * CalendarDayCell - Cel·la buida per cada dia/habitació
 * Serveix com a fons i zona clicable
 *
 * Colors de `CALENDAR_PALETTE` (`plans/calendar-visual-refresh.md` §3): lliure, DV-DS (D5), avui.
 * Quan el ratolí és sobre la fila (`group/row` de la graella), les cel·les que no són d'avui passen
 * a `rowHover`. Els colors van per variables CSS perquè el hover de la fila els pugui canviar.
 *
 * Mobile-first, modular
 */

import type { MouseEvent } from 'react';
import { cn } from '@/lib/utils';
import { CALENDAR_PALETTE } from './calendar-theme';

export interface CalendarDayCellProps {
  date: Date;
  isToday?: boolean;
  /** Divendres o dissabte (D5): les nits fortes. */
  isWeekend?: boolean;
  /** `event`: per saber a quina meitat del dia s'ha clicat (mig dia, D4). */
  onClick?: (date: Date, event: MouseEvent<HTMLDivElement>) => void;
  className?: string;
  style?: React.CSSProperties;
}

/** El color de fons d'una cel·la lliure segons el dia. */
function dayCellBackground(isToday: boolean, isWeekend: boolean): string {
  if (isToday) return CALENDAR_PALETTE.cellToday;
  if (isWeekend) return CALENDAR_PALETTE.cellWeekend;
  return CALENDAR_PALETTE.cell;
}

export function CalendarDayCell({
  date,
  isToday = false,
  isWeekend = false,
  onClick,
  className,
  style,
}: CalendarDayCellProps) {
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    onClick?.(date, event);
  };

  const background = dayCellBackground(isToday, isWeekend);
  // Avui es queda d'avui encara que la fila estigui en hover.
  const hoverBackground = isToday ? background : CALENDAR_PALETTE.rowHover;

  return (
    <div
      onClick={handleClick}
      data-today={isToday || undefined}
      data-weekend={isWeekend || undefined}
      style={
        {
          ...style,
          '--cal-cell-bg': background,
          '--cal-cell-hover-bg': hoverBackground,
          borderRightColor: CALENDAR_PALETTE.separator,
        } as React.CSSProperties
      }
      className={cn(
        // Base
        'absolute border-r transition-colors duration-150',
        'bg-[var(--cal-cell-bg)] group-hover/row:bg-[var(--cal-cell-hover-bg)]',

        // Cursor
        onClick && 'cursor-pointer',

        className,
      )}
    />
  );
}

export default CalendarDayCell;
